import { getDoc, onSnapshot } from "firebase/firestore";
import { ExamInterface, ExamSettingsInterface, ExerciseInterface } from "../interfaces/examInterfaces";
import FirebaseService, { COLLECTIONS, DIRECTORIES } from "./firebaseService";
import { EXAM_OVERVIEW_TYPES } from "../components/ExamOverview/ExamOverviewReducer";
import { Grade } from "../types/App";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import { MAIN_TYPES } from "../components/ExamBuilder/Reducers/MainReducer";
import { PRINT_TYPES } from "../components/ExamBuilder/Reducers/PrintReducer";
import { CREATE_PAGES_TYPES } from "../components/ExamBuilder/Reducers/CreatePagesReducer";

const storage = getStorage();

class FirebaseExamsService {
  async fetchExam({ userId, examId }: { userId: string; examId: string }) {
    const collectionName = COLLECTIONS.EXAMS;
    return FirebaseService.fetchItem({ userId, itemId: examId, collectionName });
  }

  async insertExam({
    userId,
    pathString,
    exam,
    index,
  }: {
    userId: string;
    pathString: string;
    exam: ExamInterface;
    index?: number;
  }): Promise<string> {
    const collectionName = COLLECTIONS.EXAMS;
    const directories = DIRECTORIES.EXAM_DIRECTORIES;
    return FirebaseService.insertItem({ userId, pathString, item: exam, index, collectionName, directories });
  }

  async insertFolder({ userId, pathString, name }: { userId: string; pathString: string; name: string }) {
    const directories = DIRECTORIES.EXAM_DIRECTORIES;
    return FirebaseService.insertFolder({ userId, pathString, name, directories });
  }

  async updateExamSubcolletion({ userId, exam }: { userId: string; exam: ExamInterface }) {
    const collectionName = COLLECTIONS.EXAMS;
    return FirebaseService.updateItemSubcolletion({ userId, item: exam, collectionName });
  }

  async updateExamSettings({ userId, examSettings }: { userId: string; examSettings: ExamSettingsInterface }) {
    return await FirebaseService.updateUserField({
      userId,
      field: "examSettings",
      data: examSettings,
    });
  }

  async renameExam({
    userId,
    pathString,
    examId,
    name,
  }: {
    userId: string;
    pathString: string;
    examId: string;
    name: string;
  }) {
    const collectionName = COLLECTIONS.EXAMS;
    const directories = DIRECTORIES.EXAM_DIRECTORIES;
    return FirebaseService.renameItem({ userId, pathString, itemId: examId, name, collectionName, directories });
  }

  async deleteExam({ userId, pathString, examId }: { userId: string; pathString: string; examId: string }) {
    const collectionName = COLLECTIONS.EXAMS;
    const directories = DIRECTORIES.EXAM_DIRECTORIES;
    return FirebaseService.deleteItem({ userId, pathString, itemId: examId, collectionName, directories });
  }

  async renameFolder({
    userId,
    pathString,
    folderId,
    prevName,
    newName,
  }: {
    userId: string;
    pathString: string;
    folderId: string;
    prevName: string;
    newName: string;
  }) {
    const directories = DIRECTORIES.EXAM_DIRECTORIES;
    return FirebaseService.renameFolder({ userId, pathString, folderId, prevName, newName, directories });
  }

  async moveExam({
    userId,
    pathString,
    newPathString,
    examId,
  }: {
    userId: string;
    pathString: string;
    newPathString: string;
    examId: string;
  }) {
    const directories = DIRECTORIES.EXAM_DIRECTORIES;
    return FirebaseService.moveItem({ userId, pathString, newPathString, itemId: examId, directories });
  }

  async moveFolder({
    userId,
    pathString,
    newPathString,
    folderId,
  }: {
    userId: string;
    pathString: string;
    newPathString: string;
    folderId: string;
  }) {
    const directories = DIRECTORIES.EXAM_DIRECTORIES;
    return FirebaseService.moveFolder({ userId, pathString, newPathString, folderId, directories });
  }

  async moveAndRenameExam({
    userId,
    pathString,
    newPathString,
    examId,
    newName,
  }: {
    userId: string;
    pathString: string;
    newPathString: string;
    examId: string;
    newName: string;
  }) {
    const directories = DIRECTORIES.EXAM_DIRECTORIES;
    return FirebaseService.moveAndRenameItem({
      userId,
      pathString,
      newPathString,
      itemId: examId,
      directories,
      newName,
    });
  }

  async moveAndRenameFolder({
    userId,
    pathString,
    newPathString,
    folderId,
    newName,
  }: {
    userId: string;
    pathString: string;
    newPathString: string;
    folderId: string;
    newName: string;
  }) {
    const directories = DIRECTORIES.EXAM_DIRECTORIES;
    return FirebaseService.moveAndRenameFolder({ userId, pathString, newPathString, folderId, directories, newName });
  }

  async deleteFolder({
    userId,
    pathString,
    folderId,
    name,
  }: {
    userId: string;
    pathString: string;
    folderId: string;
    name: string;
  }) {
    const directories = DIRECTORIES.EXAM_DIRECTORIES;
    return FirebaseService.deleteFolder({ userId, pathString, folderId, name, directories });
  }

  subscribeToUserDoc = ({ userId, dispatch }: { userId: string; dispatch: any }) => {
    if (userId) {
      dispatch({ type: EXAM_OVERVIEW_TYPES.PATHS_LOADING, payload: true });
      return onSnapshot(FirebaseService._getUserRef({ userId }), (doc) => {
        if (doc.exists()) {
          if (doc.data().examDirectories) {
            dispatch({ type: EXAM_OVERVIEW_TYPES.PATHS_UPDATED, payload: doc.data().examDirectories });
          } else {
            dispatch({ type: EXAM_OVERVIEW_TYPES.PATHS_UPDATED, payload: {} });
          }
          if (doc.data().examAccess) {
            dispatch({ type: EXAM_OVERVIEW_TYPES.EXAM_ACCESS_UPDATED, payload: doc.data().examAccess });
          } else {
            dispatch({ type: EXAM_OVERVIEW_TYPES.EXAM_ACCESS_UPDATED, payload: {} });
          }
          if (doc.data().examSettings) {
            dispatch({ type: PRINT_TYPES.CREATE_PAGE_NUMBERS, payload: doc.data().examSettings.createPageNumbers });
            dispatch({ type: PRINT_TYPES.PRINT_SOLUTIONS, payload: doc.data().examSettings.printSolutions });
            dispatch({ type: CREATE_PAGES_TYPES.LAYOUT_TYPE, payload: doc.data().examSettings.selectedLayout });
          }
          dispatch({ type: EXAM_OVERVIEW_TYPES.PATHS_LOADING, payload: false });
        }
      });
    } else {
      throw new Error(`User doc does not exists for uid ${userId}.`);
    }
  };
}

export const loadExam = async ({ userId, examId, dispatch }: { userId: string; examId: string; dispatch: any }) => {
  dispatch({ type: MAIN_TYPES.EXAM_LOADING, payload: true });

  try {
    const exam = await fetchExam({ userId, examId });
    dispatch({ type: MAIN_TYPES.EXAM_DATA, payload: exam });
    dispatch({ type: MAIN_TYPES.EXAM_LOADING, payload: false });
  } catch (error) {
    console.error("Failed to load exam:", error);
  } finally {
  }
};

const fetchExam = async ({ userId, examId }: { userId: string; examId: string }) => {
  const collectionName = "exams";
  const examDoc = await getDoc(FirebaseService._getItemRef({ userId, itemId: examId, collectionName }));
  if (!examDoc.exists) {
    throw new Error(`Exam ${examId} does not exist for user ${userId}.`);
  } else {
    return examDoc.data();
  }
};

export const fetchTopics = async ({ grade }: { grade: Grade }) => {
  const topicsDoc = await getDoc(FirebaseService._getTopicsRef({ grade }));
  if (!topicsDoc.exists) {
    throw new Error(`No topics exist for grade ${grade}.`);
  } else {
    return topicsDoc.data();
  }
};

export const fetchExercises = async ({ grade, objectiveId }: { grade: Grade; objectiveId: string }) => {
  const exerciseDoc = await getDoc(FirebaseService._getExercisesRef({ grade, objectiveId }));
  if (!exerciseDoc.exists) {
    throw new Error(`No topics exist for grade ${grade}.`);
  } else {
    return exerciseDoc.data();
  }
};

export const fetchImageUrls = async (
  exercises: ExerciseInterface[],
  getPath: (exercise: ExerciseInterface) => string
) => {
  const urls: Record<string, string> = {};

  for (const exercise of exercises) {
    const storagePath = getPath(exercise);
    const storageRef = ref(storage, storagePath);
    try {
      const url = await getDownloadURL(storageRef);
      urls[exercise.id] = url;
    } catch (error) {
      console.error(`Error fetching image URL for ${exercise.imagePath}:`, error);
    }
  }

  return urls;
};

export const fetchImageUrl = async (storagePath: string) => {
  const storageRef = ref(storage, storagePath);
  try {
    const url = await getDownloadURL(storageRef);
    return url;
  } catch (error) {
    console.error(`Error fetching image URL for ${storagePath}:`, error);
  }
};

export default new FirebaseExamsService();
